import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Лента записи по дням (CompRecord.dc.html): звонки, письма, комментарии, задачи и смены
 * стадий одним потоком.
 *
 * Один поток, а не вкладка на каждый вид: чтобы понять, почему запись встала, надо видеть,
 * что звонок был **до** возврата на переговоры, а комментарий — **после**. Фильтр сужает
 * поток, а не разбивает его на списки с разными форматами времени.
 *
 * День считается в поясе пользователя: событие в 23:30 по Москве — «сегодня», а не
 * «завтра по UTC».
 */
export type FeedKind =
  | 'call'
  | 'mail'
  | 'meeting'
  | 'comment'
  | 'note'
  | 'task'
  | 'stage'
  | 'created'
  | 'movement'
  | 'change'
  | 'document'
  | 'attachment'
  | 'approval'
  /** Сообщение мессенджера (#125). */
  | 'message'

export interface FeedItem {
  id: string
  kind: FeedKind
  /** Момент события, ISO-строка с поясом. */
  at: string
  title: ReactNode
  /** Кто или с кем. */
  who?: ReactNode
  body?: ReactNode
}

export type FeedFilter = 'all' | 'touches' | 'comments' | 'documents' | 'changes'

const FILTERS: FeedFilter[] = ['all', 'touches', 'comments', 'documents', 'changes']

const FILTER_KINDS: Record<Exclude<FeedFilter, 'all'>, FeedKind[]> = {
  touches: ['call', 'mail', 'meeting', 'message'],
  comments: ['comment', 'note'],
  // Paper and files (#114): what was issued to the customer, attached, put up for approval.
  documents: ['document', 'attachment', 'approval'],
  changes: ['stage', 'task', 'created', 'movement', 'change'],
}

const ICONS: Record<FeedKind, { icon: IconName; tone: string }> = {
  call: { icon: 'phone', tone: 'call' },
  mail: { icon: 'mail', tone: 'mail' },
  meeting: { icon: 'user', tone: '' },
  comment: { icon: 'comment', tone: '' },
  note: { icon: 'comment', tone: '' },
  task: { icon: 'work', tone: '' },
  stage: { icon: 'flag', tone: 'stage' },
  created: { icon: 'plus', tone: '' },
  movement: { icon: 'box', tone: '' },
  change: { icon: 'edit', tone: '' },
  document: { icon: 'file', tone: '' },
  attachment: { icon: 'link', tone: '' },
  approval: { icon: 'sign', tone: '' },
  message: { icon: 'comment', tone: 'mail' },
}

export interface FeedProps {
  items: FeedItem[]
  /** Что показать, когда событий нет вовсе (не «по фильтру»). */
  empty: ReactNode
  /** Под лентой: «показаны последние N». */
  footer?: ReactNode
  /** Заголовок над лентой; фильтр стоит рядом с ним. */
  title?: ReactNode
}

export function Feed({ items, empty, footer, title }: FeedProps) {
  const { t, locale } = useApp()
  const [filter, setFilter] = useState<FeedFilter>('all')

  const visible = useMemo(() => {
    const sorted = [...items].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    return filter === 'all' ? sorted : sorted.filter((item) => FILTER_KINDS[filter].includes(item.kind))
  }, [items, filter])

  const groups = useMemo(() => groupByDay(visible), [visible])

  return (
    <section className="stack" style={{ gap: 8 }} aria-label={t('record.feed.title')}>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span className="h2">{title ?? t('record.feed.title')}</span>
        {items.length > 0 && (
          <div className="seg" role="group" aria-label={t('record.feed.filter')}>
            {FILTERS.map((id) => (
              <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>
                {t(`record.feed.filter.${id}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="panel">{empty}</div>
      ) : visible.length === 0 ? (
        <p className="secondary t-sm">{t('record.feed.noneByFilter')}</p>
      ) : (
        <ol className="feed" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {groups.map((group, groupIndex) => (
            <Fragment key={group.key}>
              <li className="feed-day">
                {dayLabel(group.date, locale, t)}
              </li>
              {group.items.map((item, index) => {
                const last = groupIndex === groups.length - 1 && index === group.items.length - 1
                const look = ICONS[item.kind]
                return (
                  <li
                    key={`${item.kind}-${item.id}`}
                    // The anchor a summary's source link scrolls to (#130).
                    id={`feed-event-${item.id}`}
                    className={last ? 'feed-item is-last' : 'feed-item'}
                  >
                    <span className={`feed-ico ${look.tone}`.trim()}>
                      <Icon name={look.icon} />
                    </span>
                    <div className="stack" style={{ minWidth: 0 }}>
                      <div className="feed-head">
                        <span className="strong">{item.title}</span>
                        {item.who && <span>{item.who}</span>}
                        <time className="t" dateTime={item.at}>
                          {timeOf(item.at, locale)}
                        </time>
                      </div>
                      {item.body && <div className="feed-body">{item.body}</div>}
                    </div>
                  </li>
                )
              })}
            </Fragment>
          ))}
        </ol>
      )}
      {footer}
    </section>
  )
}

interface DayGroup {
  key: string
  date: Date
  items: FeedItem[]
}

function groupByDay(items: FeedItem[]): DayGroup[] {
  const groups: DayGroup[] = []
  for (const item of items) {
    const moment = new Date(item.at)
    const key = Number.isNaN(moment.getTime())
      ? 'unknown'
      : `${moment.getFullYear()}-${moment.getMonth()}-${moment.getDate()}`
    const current = groups[groups.length - 1]
    if (current && current.key === key) current.items.push(item)
    else groups.push({ key, date: moment, items: [item] })
  }
  return groups
}

/** «Сегодня», «Вчера, 08.10», иначе полная дата: так ленту читают, не считая дни. */
export function dayLabel(
  date: Date,
  locale: string,
  t: (key: string, params?: Record<string, string | number>) => string,
  now: Date = new Date(),
): string {
  if (Number.isNaN(date.getTime())) return '—'
  const tag = locale === 'en' ? 'en-GB' : 'ru-RU'
  const startOf = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  const days = Math.round((startOf(now) - startOf(date)) / 86_400_000)
  if (days === 0) return t('record.feed.today')
  if (days === 1) {
    return t('record.feed.yesterday', {
      date: date.toLocaleDateString(tag, { day: '2-digit', month: '2-digit' }),
    })
  }
  return date.toLocaleDateString(tag, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function timeOf(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleTimeString(locale === 'en' ? 'en-GB' : 'ru-RU', { hour: '2-digit', minute: '2-digit' })
}
